import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import BetaForside from "./BetaForside";
import MobileShell, { useIsMobile } from "../components/MobileShell";
import { Eye, EyeOff, Sparkles, Gift } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const Tabs = ({ tab, setTab }) => {
  const { t } = useTranslation();
  return (
    <div className="flex gap-6 border-b border-white/10 mb-8">
      {[
        { k: "login",    label: t("auth.log_in_btn") },
        { k: "register", label: t("auth.create_account") },
      ].map((tt) => (
        <button key={tt.k}
          onClick={() => setTab(tt.k)}
          data-testid={`auth-tab-${tt.k}`}
          className={`pb-3 font-manrope text-[11px] uppercase tracking-[0.25em] transition-colors relative ${
            tab === tt.k ? "text-[var(--cream)]" : "text-[var(--cream-soft)] hover:text-[var(--cream)]"
          }`}>
          {tt.label}
          {tab === tt.k && <span className="absolute left-0 right-0 -bottom-px h-px bg-[var(--magenta)]" />}
        </button>
      ))}
    </div>
  );
};

const RegisterPanel = () => {
  const { t } = useTranslation();
  const { register, registerCouple } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState("single");
  const urlPromoCode = (searchParams.get("kode") || searchParams.get("code") || "").trim().toUpperCase();
  const urlReferralCode = (searchParams.get("ref") || searchParams.get("verver") || "").trim().toUpperCase();
  const [form, setForm] = useState({
    username: "", email: "", password: "",
    is_adult: false, accept_terms: false,
    promo_code: urlPromoCode, referral_code: urlReferralCode,
  });
  const [coupleForm, setCoupleForm] = useState({
    partner_a_email: "", partner_b_email: "", couple_username: "", couple_name: "",
    password: "", is_adult: false, accept_terms: false,
    partner_a_gender: "", partner_a_age: "",
    partner_b_gender: "", partner_b_age: "",
    referral_code: urlReferralCode,
  });

  // Persister referral/promo i sessionStorage så de overlever tab-bytte
  useEffect(() => {
    if (urlReferralCode) sessionStorage.setItem("ne_referral_code", urlReferralCode);
    else {
      const saved = sessionStorage.getItem("ne_referral_code");
      if (saved && !form.referral_code) {
        setForm((s) => ({ ...s, referral_code: saved }));
        setCoupleForm((s) => ({ ...s, referral_code: saved }));
      }
    }
    if (urlPromoCode) sessionStorage.setItem("ne_promo_code", urlPromoCode);
    else {
      const savedP = sessionStorage.getItem("ne_promo_code");
      if (savedP && !form.promo_code) setForm((s) => ({ ...s, promo_code: savedP }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));
  const setC = (k) => (v) => setCoupleForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.is_adult)     return setError(t("auth.confirm_age_required"));
    if (!form.accept_terms) return setError(t("auth.accept_terms_required"));
    if (TURNSTILE_SITE_KEY && !turnstileToken) return setError(t("auth.captcha_required"));
    setLoading(true);
    try {
      await register({ ...form, turnstile_token: turnstileToken || undefined });
      sessionStorage.removeItem("ne_referral_code");
      sessionStorage.removeItem("ne_promo_code");
      navigate("/");
    }
    catch (err) {
      setError(formatApiError(err));
      if (turnstileRef.current?.reset) turnstileRef.current.reset();
      setTurnstileToken("");
    }
    finally { setLoading(false); }
  };

  const submitCouple = async (e) => {
    e.preventDefault();
    setError("");
    if (!coupleForm.is_adult)     return setError(t("auth.couple_both_18"));
    if (!coupleForm.accept_terms) return setError(t("auth.accept_terms_required"));
    if (coupleForm.partner_a_email.toLowerCase().trim() === coupleForm.partner_b_email.toLowerCase().trim()) {
      return setError(t("auth.couple_emails_different"));
    }
    if (!coupleForm.partner_a_gender || !coupleForm.partner_b_gender) {
      return setError(t("couple_reg.err_gender"));
    }
    const ageA = parseInt(coupleForm.partner_a_age, 10);
    const ageB = parseInt(coupleForm.partner_b_age, 10);
    if (!(ageA >= 18 && ageA <= 120) || !(ageB >= 18 && ageB <= 120)) {
      return setError(t("couple_reg.err_age"));
    }
    setLoading(true);
    try {
      await registerCouple({ ...coupleForm, partner_a_age: ageA, partner_b_age: ageB });
      sessionStorage.removeItem("ne_referral_code");
      navigate("/par-innstillinger");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (mode === "couple") {
    return (
      <div className="space-y-5" data-testid="register-form-couple-wrapper">
        <div className="flex gap-3 items-center text-[10px] font-manrope uppercase tracking-[0.2em]">
          <button type="button" onClick={() => setMode("single")}
                  className="text-[var(--cream-soft)] hover:text-[var(--cream)]"
                  data-testid="register-mode-back-single">
            {t("couple_reg.single")}
          </button>
          <span className="text-[var(--cream)] font-medium">{t("couple_reg.couple_account")}</span>
        </div>
        <form onSubmit={submitCouple} className="space-y-5" data-testid="register-form-couple">
          <div className="bg-[var(--magenta)]/[0.06] border-l-2 border-[var(--magenta)] px-3 py-2.5">
            <p className="font-manrope text-[11px] text-[var(--cream)] leading-relaxed" dangerouslySetInnerHTML={{ __html: t("couple_reg.intro") }} />
          </div>
          <div>
            <div className="font-meta mb-2">{t("couple_reg.username")}</div>
            <input value={coupleForm.couple_username} onChange={(e) => setC("couple_username")(e.target.value)}
                   className="input-editorial" placeholder="LarsOgMari"
                   required minLength={3} maxLength={24} data-testid="couple-username" />
          </div>
          <div>
            <div className="font-meta mb-2">{t("couple_reg.name")}</div>
            <input value={coupleForm.couple_name} onChange={(e) => setC("couple_name")(e.target.value)}
                   className="input-editorial" placeholder="Lars & Mari"
                   required minLength={2} maxLength={60} data-testid="couple-name" />
          </div>
          <div>
            <div className="font-meta mb-2">{t("couple_reg.email_a")}</div>
            <input type="email" value={coupleForm.partner_a_email} onChange={(e) => setC("partner_a_email")(e.target.value)}
                   className="input-editorial" required data-testid="couple-email-a" />
            <div className="grid grid-cols-2 gap-3 mt-2">
              <select value={coupleForm.partner_a_gender} onChange={(e) => setC("partner_a_gender")(e.target.value)}
                      className="input-editorial" required data-testid="couple-gender-a">
                <option value="">{t("couple_reg.gender_a")}</option>
                <option value="kvinne">{t("couple_reg.woman")}</option>
                <option value="mann">{t("couple_reg.man")}</option>
                <option value="trans">{t("couple_reg.trans")}</option>
                <option value="annet">{t("couple_reg.other")}</option>
              </select>
              <input type="number" min="18" max="120" value={coupleForm.partner_a_age}
                     onChange={(e) => setC("partner_a_age")(e.target.value)}
                     className="input-editorial" placeholder={t("couple_reg.age_a")} required data-testid="couple-age-a" />
            </div>
          </div>
          <div>
            <div className="font-meta mb-2">{t("couple_reg.email_b")}</div>
            <input type="email" value={coupleForm.partner_b_email} onChange={(e) => setC("partner_b_email")(e.target.value)}
                   className="input-editorial" required data-testid="couple-email-b" />
            <div className="grid grid-cols-2 gap-3 mt-2">
              <select value={coupleForm.partner_b_gender} onChange={(e) => setC("partner_b_gender")(e.target.value)}
                      className="input-editorial" required data-testid="couple-gender-b">
                <option value="">{t("couple_reg.gender_b")}</option>
                <option value="kvinne">{t("couple_reg.woman")}</option>
                <option value="mann">{t("couple_reg.man")}</option>
                <option value="trans">{t("couple_reg.trans")}</option>
                <option value="annet">{t("couple_reg.other")}</option>
              </select>
              <input type="number" min="18" max="120" value={coupleForm.partner_b_age}
                     onChange={(e) => setC("partner_b_age")(e.target.value)}
                     className="input-editorial" placeholder={t("couple_reg.age_b")} required data-testid="couple-age-b" />
            </div>
          </div>
          <div>
            <div className="font-meta mb-2">{t("couple_reg.password")}</div>
            <input type="password" value={coupleForm.password} onChange={(e) => setC("password")(e.target.value)}
                   className="input-editorial" required minLength={6} data-testid="couple-password" />
            <p className="font-manrope text-[10px] text-[var(--cream-faint)] mt-2 uppercase tracking-[0.15em]">
              {t("couple_reg.password_hint")}
            </p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer text-sm text-[var(--cream)] font-manrope">
            <input type="checkbox" checked={coupleForm.is_adult}
                   onChange={(e) => { const c = e.target.checked; setC("is_adult")(c); setC("accept_terms")(c); }}
                   className="mt-0.5 h-4 w-4 accent-[var(--magenta)] cursor-pointer" data-testid="couple-terms" />
            <span dangerouslySetInnerHTML={{ __html: t("couple_reg.confirm") }} />
          </label>
          {error && <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3" data-testid="register-couple-error">{error}</div>}
          <button type="submit" disabled={loading} className="btn-magenta w-full" data-testid="register-couple-submit">
            {loading ? t("couple_reg.creating") : t("couple_reg.create")}
          </button>
        </form>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="register-form">
      <div className="flex items-center justify-between mb-1">
        <button type="button" onClick={() => setMode("couple")}
                className="text-[10px] font-manrope uppercase tracking-[0.2em] text-[var(--magenta)] hover:text-[var(--cream)] inline-flex items-center gap-1"
                data-testid="register-mode-go-couple">
          {t("auth.couple_intro_title", "Er dere et par? Opprett par-konto")} →
        </button>
      </div>
      {(form.referral_code || form.promo_code) && (
        <div className="border border-[var(--magenta)]/40 bg-[var(--magenta)]/[0.06] p-3 space-y-1.5" data-testid="prefill-banner">
          {form.referral_code && (
            <div className="flex items-center gap-2 font-manrope text-xs text-[var(--cream)]" data-testid="referral-banner">
              <Sparkles className="h-3.5 w-3.5 text-[var(--magenta)]" />
              <span>{t("auth.referral_prefilled", { handle: form.referral_code })}</span>
            </div>
          )}
          {form.promo_code && (
            <div className="flex items-center gap-2 font-manrope text-xs text-[var(--cream)]" data-testid="promo-banner">
              <Gift className="h-3.5 w-3.5 text-[var(--gold)]" />
              <span>{t("auth.code_prefilled", { code: form.promo_code })}</span>
            </div>
          )}
        </div>
      )}
      <div>
        <div className="font-meta mb-2">{t("auth.username")}</div>
        <input value={form.username} onChange={(e)=>set("username")(e.target.value)} className="input-editorial"
               placeholder={t("auth.anonymous_name")} required minLength={3} maxLength={24} data-testid="register-username" />
      </div>
      <div>
        <div className="font-meta mb-2">{t("auth.email")}</div>
        <input type="email" value={form.email} onChange={(e)=>set("email")(e.target.value)} className="input-editorial"
               required data-testid="register-email" />
      </div>
      <div>
        <div className="font-meta mb-2">{t("auth.password")}</div>
        <div className="relative">
          <input type={showPw ? "text" : "password"} value={form.password} onChange={(e)=>set("password")(e.target.value)} className="input-editorial pr-10"
                 required minLength={6} data-testid="register-password" />
          <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
                  aria-label={showPw ? t("auth.hide_password") : t("auth.show_password")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[var(--cream-soft)] hover:text-[var(--magenta)] transition-colors"
                  data-testid="register-password-toggle">
            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div>
        <div className="font-meta mb-2">{t("auth.have_code_optional")}</div>
        <input value={form.promo_code} onChange={(e)=>set("promo_code")(e.target.value.toUpperCase())}
               className="input-editorial uppercase tracking-wider"
               placeholder={t("auth.gift_code_placeholder")} maxLength={20}
               data-testid="register-promo-code" />
      </div>
      <div className="space-y-3 pt-2">
        <label className="flex items-start gap-3 cursor-pointer text-sm text-[var(--cream)] font-manrope">
          <input type="checkbox" checked={form.is_adult}
                 onChange={(e)=>{ const c=e.target.checked; set("is_adult")(c); set("accept_terms")(c); }}
                 className="mt-0.5 h-4 w-4 accent-[var(--magenta)] cursor-pointer" data-testid="register-terms" />
          <span dangerouslySetInnerHTML={{ __html: t("auth.i_confirm_18") }} />
        </label>
      </div>
      {error && <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3" data-testid="register-error">{error}</div>}
      {TURNSTILE_SITE_KEY && (
        <div className="flex justify-center" data-testid="register-turnstile">
          <Turnstile
            ref={turnstileRef}
            siteKey={TURNSTILE_SITE_KEY}
            onSuccess={(token) => setTurnstileToken(token)}
            onError={() => setTurnstileToken("")}
            onExpire={() => setTurnstileToken("")}
            options={{ theme: "dark", language: "nb" }}
          />
        </div>
      )}
      <button type="submit" disabled={loading} className="btn-magenta w-full" data-testid="register-submit">
        {loading ? t("auth.creating") : t("auth.create_account")}
      </button>
      <p className="text-xs text-[var(--cream-faint)] font-manrope leading-relaxed text-center pt-1" data-testid="register-passive-consent">
        {t("auth.terms_full")}{" "}
        <Link to="/vilkar" target="_blank" rel="noreferrer" className="text-[var(--cream-soft)] hover:text-[var(--magenta)] underline-offset-4 hover:underline">{t("auth.tos_link")}</Link>,{" "}
        <Link to="/personvern" target="_blank" rel="noreferrer" className="text-[var(--cream-soft)] hover:text-[var(--magenta)] underline-offset-4 hover:underline">{t("auth.privacy_link")}</Link>,{" "}
        <Link to="/cookies" target="_blank" rel="noreferrer" className="text-[var(--cream-soft)] hover:text-[var(--magenta)] underline-offset-4 hover:underline">{t("auth.cookies_link")}</Link>{" "}&{" "}
        <Link to="/husregler" target="_blank" rel="noreferrer" className="text-[var(--cream-soft)] hover:text-[var(--magenta)] underline-offset-4 hover:underline">{t("auth.house_rules_link")}</Link>.
      </p>
    </form>
  );
};

const LoginPanel = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [emailUnverified, setEmailUnverified] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setEmailUnverified(false); setLoading(true);
    try { await login(email, password); navigate("/"); }
    catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      if (/ikke.*bekreftet|not.*verified|verifisert/i.test(msg)) {
        setEmailUnverified(true);
      }
    }
    finally { setLoading(false); }
  };

  const resendVerification = async () => {
    if (!email) {
      toast.error(t("auth.enter_email_first", "Skriv inn e-postadressen din først"));
      return;
    }
    setResendBusy(true);
    try {
      await api.post("/auth/verify-email/resend", { email });
      toast.success(t("auth.verify_email_sent"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="login-form">
      <div>
        <div className="font-meta mb-2">{t("auth.email")}</div>
        <input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} className="input-editorial"
               required data-testid="login-email" />
      </div>
      <div>
        <div className="font-meta mb-2">{t("auth.password")}</div>
        <div className="relative">
          <input type={showPw ? "text" : "password"} value={password} onChange={(e)=>setPassword(e.target.value)} className="input-editorial pr-10"
                 required data-testid="login-password" />
          <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
                  aria-label={showPw ? t("auth.hide_password") : t("auth.show_password")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[var(--cream-soft)] hover:text-[var(--magenta)] transition-colors"
                  data-testid="login-password-toggle">
            {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {error && (
        <div className="text-sm text-[var(--cream)] font-medium border-l-2 border-[var(--magenta)] pl-3" data-testid="login-error">
          {error}
          {emailUnverified && (
            <div className="mt-3 pt-3 border-t border-[var(--magenta)]/30">
              <button
                type="button"
                onClick={resendVerification}
                disabled={resendBusy}
                className="text-[var(--cream)] underline underline-offset-4 hover:text-[var(--magenta)] disabled:opacity-50"
                data-testid="login-resend-verification-btn"
              >
                {resendBusy ? t("auth.sending", "Sender…") : t("auth.verify_email_sent")}
              </button>
            </div>
          )}
        </div>
      )}
      <button type="submit" disabled={loading} className="btn-magenta w-full" data-testid="login-submit">
        {loading ? t("auth.logging_in") : t("auth.log_in_btn")}
      </button>
      <p className="font-manrope text-[11px] text-[var(--cream-soft)] uppercase tracking-[0.2em] text-center pt-2">
        {t("auth.password_forgotten")} <Link to="/glemt-passord" className="text-[var(--magenta)] hover:underline" data-testid="forgot-password-link">{t("auth.reset_password")}</Link>
      </p>
    </form>
  );
};

const AuthPanel = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("tab") === "register" ? "register" : "login";
  const [tab, setTab] = useState(initial);
  const switchTab = (k) => {
    setTab(k);
    const next = new URLSearchParams(searchParams);
    if (k === "register") next.set("tab", "register"); else next.delete("tab");
    setSearchParams(next, { replace: true });
  };
  return (
    <div className="editorial-card p-8 sm:p-10">
      <Tabs tab={tab} setTab={switchTab} />
      {tab === "register" ? <RegisterPanel /> : <LoginPanel />}
    </div>
  );
};

export default function Home() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const isAuth = user && user !== false;
  // Mobil-app-UI er kun for owner under lukket beta.
  const isOwner = user?.role === "owner";
  const useMobileShell = isMobile && isOwner;

  // Innlogget: ny Hjem-feed
  if (isAuth) return <BetaForside />;

  // Mobil ikke-innlogget: kompakt hero + AuthPanel i MobileShell (kun owner-testing)
  if (!isAuth && useMobileShell) {
    return (
      <MobileShell>
        <section className="px-4 py-6">
          <div className="font-meta mb-4 text-[var(--cream-soft)]" data-testid="hero-vol">{t("home.hero_vol")}</div>
          <h1 className="font-fraunces italic text-[var(--magenta)] text-4xl leading-[0.95] mb-3 tracking-tight" data-testid="hero-brand">
            Norsk Erotikk
          </h1>
          <p className="font-fraunces italic text-[var(--cream)] text-lg mb-6 leading-snug">
            {t("home.hero_subtitle")}
          </p>
          <AuthPanel />
          {stats && (
            <div className="mt-6 flex flex-wrap gap-x-4 gap-y-1 font-manrope text-[10px] uppercase tracking-[0.2em] text-[var(--cream-soft)]">
              <span><span className="text-[var(--cream)] font-semibold">{stats.users}</span> {t("home.hero_stat_members")}</span>
              <span className="text-white/15">·</span>
              <span><span className="text-[var(--cream)] font-semibold">{stats.threads}</span> {t("home.hero_stat_threads")}</span>
              <span className="text-white/15">·</span>
              <span><span className="text-[var(--cream)] font-semibold">{stats.posts}</span> {t("home.hero_stat_posts")}</span>
            </div>
          )}
        </section>
      </MobileShell>
    );
  }

  // Ikke-innlogget: hero + auth-panel + minimal footer
  return (
    <div className="min-h-screen flex flex-col">
      <div className="viking-side-left"  aria-hidden="true" />
      <div className="viking-side-right" aria-hidden="true" />
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-32 -right-32 h-[600px] w-[600px] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(247,24,104,0.10), transparent 60%)" }} />
        <div className="absolute -bottom-32 -left-32 h-[500px] w-[500px] rounded-full pointer-events-none"
             style={{ background: "radial-gradient(circle, rgba(178,106,63,0.06), transparent 60%)" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 py-14 sm:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="font-meta mb-8" data-testid="hero-vol">{t("home.hero_vol")}</div>
              <h1 className="font-fraunces italic text-[var(--magenta)] text-[48px] sm:text-[88px] lg:text-[112px] leading-[0.95] mb-4 tracking-tight"
                  data-testid="hero-brand">
                Norsk Erotikk
              </h1>
              <div className="relative font-fraunces italic text-[var(--cream)] text-2xl sm:text-3xl mb-10 max-w-lg leading-snug">
                {t("home.hero_subtitle")}
              </div>

              <ul className="space-y-4 mb-10 max-w-md">
                {[
                  ["I",   t("home.hero_bullet_1")],
                  ["II",  t("home.hero_bullet_2")],
                  ["III", t("home.hero_bullet_3")],
                  ["IV",  t("home.hero_bullet_4")],
                ].map(([n, txt]) => (
                  <li key={n} className="flex gap-5 items-start">
                    <span className="font-fraunces italic text-[var(--magenta)] text-xl pt-0.5 w-8 shrink-0">{n}.</span>
                    <span className="font-manrope text-[var(--cream)] text-[15px] leading-relaxed">{txt}</span>
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-6 font-manrope text-[11px] uppercase tracking-[0.25em] text-[var(--cream-soft)]">
                {stats && (
                  <>
                    <span><span className="text-[var(--cream)] font-semibold">{stats.users}</span> {t("home.hero_stat_members")}</span>
                    <span className="text-white/15">·</span>
                    <span><span className="text-[var(--cream)] font-semibold">{stats.threads}</span> {t("home.hero_stat_threads")}</span>
                    <span className="text-white/15">·</span>
                    <span><span className="text-[var(--cream)] font-semibold">{stats.posts}</span> {t("home.hero_stat_posts")}</span>
                  </>
                )}
              </div>
            </div>

            <div className="lg:pl-6">
              <AuthPanel />
            </div>
          </div>
        </div>
      </section>

      {/* FORSIDEN: kun HERO + auth-panel. Resten av siden vises etter innlogging. */}

      <footer className="border-t border-white/10 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <div className="font-manrope text-[11px] uppercase tracking-[0.25em] text-[var(--cream-soft)]">
            Norsk Erotikk · Voksent fellesskap · 18+ · Norge · 2026
          </div>
          <Link to="/personvern" className="font-meta hover:text-[var(--cream)]">
            Vilkår & personvern
          </Link>
        </div>
      </footer>
    </div>
  );
}
